import { Card } from "@/components/ui/card";
import { Package, Plane, Clock, CheckCircle } from "lucide-react";

interface ShippingTimeInfoProps {
  variant?: "compact" | "detailed";
}

export const ShippingTimeInfo = ({ variant = "compact" }: ShippingTimeInfoProps) => {
  if (variant === "compact") {
    return (
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-lg shadow-sm">
            <Package className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-playfair text-lg font-bold mb-2 text-primary">Delivery Options</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Plane className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Air Freight (Smaller Orders)</p>
                  <p className="text-muted-foreground">10-15 days delivery • Expedited service</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Package className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Freight Shipping (Bulk Orders)</p>
                  <p className="text-muted-foreground">Up to 40 days • Best value for large quantities</p>
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              ✓ Full tracking provided • ✓ Regular status updates • ✓ Door-to-door insured delivery
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="text-center mb-6">
        <h3 className="font-playfair text-2xl font-bold mb-2 text-primary">Shipping & Delivery Timeline</h3>
        <p className="text-muted-foreground">Choose the shipping method that best fits your needs</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Air Freight */}
        <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Plane className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-bold text-lg text-foreground">Air Freight</h4>
              <p className="text-sm text-green-600 font-semibold">Expedited Delivery</p>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-green-600" />
              <span className="font-semibold">10-15 days delivery</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Perfect for individual units, residential customers, and urgent renovation projects. 
              Fast, reliable service with premium handling.
            </p>
          </div>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Processing & prep: 1-3 days</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Air transit: 5-8 days</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Express delivery: 1-2 days</span>
            </li>
          </ul>
        </div>

        {/* Freight Shipping */}
        <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-bold text-lg text-foreground">Freight Shipping</h4>
              <p className="text-sm text-blue-600 font-semibold">Best Value</p>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="font-semibold">Up to 40 days delivery</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Ideal for bulk orders, commercial projects, and large quantities. Most economical 
              option with professional freight handling.
            </p>
          </div>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Processing & prep: 3-7 days</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Container freight: 25-35 days</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Local delivery: 2-5 days</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Communication Commitment */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
        <p className="font-semibold text-sm mb-2 text-amber-900">📦 Our Commitment to You</p>
        <p className="text-xs text-amber-800">
          Full tracking information • Regular status updates • 24-48 hour delivery advance notice • 
          Complete transparency throughout the shipping process
        </p>
      </div>
    </Card>
  );
};
